import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Trash2 } from "react-feather";
import { LoggedUser } from "components/LoggedUser";
import { SupportAgentCard } from "components/SupportAgentCard";
import { AbsenceCard } from "components/AbsenceCard";
import { SupportStatsPanel } from "components/SupportStatsPanel";
import { AddSupportAgentModal } from "components/AddSupportAgentModal";
import { AddAbsenceModal } from "components/AddAbsenceModal";
import { LoadingSpinner } from "components/LoadingSpinner";
import { Loading } from "components/ContactsTable/styles";
import { Table } from "styles/Table";
import { Button } from "styles/Button";
import { useAuth } from "hooks/useAuth";
import { useSupportAgents } from "hooks/fetch/useSupportAgents";
import { useAgentAbsences } from "hooks/fetch/useAgentAbsences";
import { useSupportShifts } from "hooks/fetch/useSupportShifts";
import { useShiftUpcomingDates } from "hooks/fetch/useShiftUpcomingDates";
import { useWaitingResponseContacts, WaitingResponseContact } from "hooks/fetch/useWaitingResponseContacts";
import { useClosedAttendances, ClosedAttendance } from "hooks/fetch/useClosedAttendances";
import { useResetRoundRobinWeights } from "hooks/mutations/useResetRoundRobinWeights";
import { useUpdateAttendanceTimes } from "hooks/mutations/useUpdateAttendanceTimes";
import { useAddSupportShift } from "hooks/mutations/useAddSupportShift";
import { useDeleteSupportShift } from "hooks/mutations/useDeleteSupportShift";
import { AdminList } from "types/User";
import { DEPARTMENT_SUPPORT_ID } from "constants/remote";
import Swal from "sweetalert2";
import { useTheme } from "styled-components";
import {
  Container,
  Content,
  PageSidebar,
  ContentTable,
  TabBar,
  Tab,
  SectionTitle,
  SidebarActions,
} from "./styles";

type ActiveTab = 'agents' | 'absences' | 'shifts' | 'waiting' | 'closed';

export function Support() {
  const { user } = useAuth();
  const theme = useTheme();

  const [activeTab, setActiveTab] = useState<ActiveTab>('agents');
  const [isAddAgentModalOpen, setIsAddAgentModalOpen] = useState(false);
  const [isAddAbsenceModalOpen, setIsAddAbsenceModalOpen] = useState(false);
  const [selectedShiftUserId, setSelectedShiftUserId] = useState('');
  const [selectedShiftDate, setSelectedShiftDate] = useState('');

  const categoryId = DEPARTMENT_SUPPORT_ID;

  const [closedFrom, setClosedFrom] = useState(() => new Date().toISOString().split('T')[0]);
  const [closedTo, setClosedTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());
  const [expandedWaitingAgents, setExpandedWaitingAgents] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTme, setEditTme] = useState('');
  const [editTma, setEditTma] = useState('');

  const { data: agents, isLoading: loadingAgents } = useSupportAgents(categoryId);
  const { data: waitingContacts, isLoading: loadingWaiting } = useWaitingResponseContacts(categoryId);
  const { data: closedAttendances, isLoading: loadingClosed } = useClosedAttendances({ from: closedFrom, to: closedTo, categoryId });
  const updateAttendanceTimes = useUpdateAttendanceTimes();
  const { data: absences, isLoading: loadingAbsences } = useAgentAbsences();
  const { data: upcomingDates, isLoading: loadingDates } = useShiftUpcomingDates();
  const shiftDate = selectedShiftDate || upcomingDates?.[0]?.date || '';
  const { data: shifts, isLoading: loadingShifts } = useSupportShifts(shiftDate);
  const resetWeights = useResetRoundRobinWeights();
  const addShift = useAddSupportShift();
  const deleteShift = useDeleteSupportShift();

  const dateShortcuts = useMemo(() => {
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    const today = new Date();
    const dow = today.getDay();
    const mondayDiff = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(today); monday.setDate(today.getDate() + mondayDiff);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    const lastMonday = new Date(monday); lastMonday.setDate(monday.getDate() - 7);
    const lastSunday = new Date(monday); lastSunday.setDate(monday.getDate() - 1);
    const firstCurrMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastCurrMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const firstLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    return [
      { label: 'Hoje', from: fmt(today), to: fmt(today) },
      { label: 'Semana atual', from: fmt(monday), to: fmt(sunday) },
      { label: 'Semana passada', from: fmt(lastMonday), to: fmt(lastSunday) },
      { label: 'Mês atual', from: fmt(firstCurrMonth), to: fmt(lastCurrMonth) },
      { label: 'Mês passado', from: fmt(firstLastMonth), to: fmt(lastLastMonth) },
    ];
  }, []);

  const activeShortcut = dateShortcuts.find(s => s.from === closedFrom && s.to === closedTo)?.label ?? null;

  const groupedWaiting = useMemo(() => {
    if (!waitingContacts) return [];
    const map = new Map<string, { agentId: string; agentName: string; contacts: WaitingResponseContact[] }>();
    for (const c of waitingContacts) {
      const key = c.agent_id || 'sem-agente';
      if (!map.has(key)) {
        map.set(key, { agentId: key, agentName: c.agent_name || 'Sem atendente', contacts: [] });
      }
      map.get(key)!.contacts.push(c);
    }
    return Array.from(map.values());
  }, [waitingContacts]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groupedAttendances = useMemo(() => {
    if (!closedAttendances) return [];
    const map = new Map<string, { agentId: string; agentName: string; records: ClosedAttendance[] }>();
    for (const row of closedAttendances) {
      if (!map.has(row.agent_id)) {
        map.set(row.agent_id, { agentId: row.agent_id, agentName: row.agent_name, records: [] });
      }
      map.get(row.agent_id)!.records.push(row);
    }
    return Array.from(map.values());
  }, [closedAttendances]);

  if (user == null || !AdminList.includes(user.name)) {
    return (
      <Container>
        <Content>
          <PageSidebar>
            <LoggedUser />
          </PageSidebar>
          <div style={{ alignSelf: 'center', justifySelf: 'center' }}>
            <h2>Você não tem permissão para acessar essa página!</h2>
          </div>
        </Content>
      </Container>
    );
  }

  async function handleResetWeights() {
    const result = await Swal.fire({
      title: 'Reiniciar rodizio?',
      text: 'Isso zerará os pesos atuais e reiniciará o rodízio do zero. Confirmar?',
      icon: 'question',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Reiniciar',
      confirmButtonColor: theme.colors.primary,
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;
    await resetWeights.mutateAsync(categoryId);
  }

  async function handleAddShift() {
    if (!selectedShiftUserId) return;
    await addShift.mutateAsync({ user_id: selectedShiftUserId, shift_date: shiftDate });
    setSelectedShiftUserId('');
  }

  async function handleDeleteShift(shift: any) {
    await deleteShift.mutateAsync(shift);
  }


  function toggleWaitingAgent(agentId: string) {
    setExpandedWaitingAgents(prev => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  }

  function toggleAgent(agentId: string) {
    setExpandedAgents(prev => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  }

  function timeToSeconds(t: string | null): number {
    if (!t) return 0;
    const [h, m, s] = t.split(':').map(Number);
    return h * 3600 + m * 60 + (s || 0);
  }

  function secondsToHHMMSS(secs: number): string {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  function formatTime(t: string | null): string {
    if (!t) return '—';
    const [h, m] = t.split(':').map(Number);
    if (h > 0) return `${h}h ${m}min`;
    return `${m}min`;
  }

  function avgTime(records: typeof closedAttendances, field: 'tme' | 'tma'): string {
    if (!records) return '—';
    const valid = records.filter(r => r[field]);
    if (!valid.length) return '—';
    const total = valid.reduce((acc, r) => acc + timeToSeconds(r[field]), 0);
    return formatTime(secondsToHHMMSS(Math.floor(total / valid.length)));
  }

  function startEdit(id: string, tme: string | null, tma: string | null) {
    setEditingId(id);
    setEditTme(tme ? tme.slice(0, 5) : '00:00');
    setEditTma(tma ? tma.slice(0, 5) : '00:00');
  }

  async function saveEdit(id: string) {
    const toHHMMSS = (hhmm: string) => hhmm.length === 5 ? `${hhmm}:00` : hhmm;
    await updateAttendanceTimes.mutateAsync({ id, tme: toHHMMSS(editTme), tma: toHHMMSS(editTma) });
    setEditingId(null);
  }

  function returnPage() {
    window.location.href = '/';
  }

  function formatWaitingTime(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    const days = Math.floor(hours / 24);
    const hrs = hours % 24;
    return hrs > 0 ? `${days}d ${hrs}h` : `${days}d`;
  }

  // Agentes ainda não cadastrados no plantão desta data
  const agentsNotInShift = (agents ?? []).filter(
    a => !(shifts ?? []).some(s => s.user_id === a.user_id)
  );

  return (
    <Container>
      <Content>
        <PageSidebar>
          <LoggedUser />

          <div>
            <SectionTitle>Distribuição</SectionTitle>
            <SupportStatsPanel categoryId={categoryId} />
          </div>

          <SidebarActions>
            <Button small fullwidth onClick={() => setIsAddAgentModalOpen(true)}>
              + Adicionar agente
            </Button>
            <Button small fullwidth onClick={() => setIsAddAbsenceModalOpen(true)}>
              + Cadastrar ausência
            </Button>
            <Button small fullwidth onClick={handleResetWeights} disabled={resetWeights.isLoading}>
              {resetWeights.isLoading ? 'Reiniciando...' : 'Reiniciar rodizio'}
            </Button>
            <Button small fullwidth onClick={() => returnPage()}>
              Voltar
            </Button>
          </SidebarActions>
        </PageSidebar>

        <ContentTable>
          <TabBar>
            <Tab active={activeTab === 'agents'} onClick={() => setActiveTab('agents')}>
              Agentes ({agents?.length ?? 0})
            </Tab>
            <Tab active={activeTab === 'absences'} onClick={() => setActiveTab('absences')}>
              Ausências ({absences?.length ?? 0})
            </Tab>
            <Tab active={activeTab === 'shifts'} onClick={() => setActiveTab('shifts')}>
              Plantão ({shifts?.length ?? 0})
            </Tab>
            <Tab active={activeTab === 'waiting'} onClick={() => setActiveTab('waiting')}>
              Aguardando ({waitingContacts?.length ?? 0})
            </Tab>
            <Tab active={activeTab === 'closed'} onClick={() => setActiveTab('closed')}>
              Encerrados ({closedAttendances?.length ?? 0})
            </Tab>
          </TabBar>

          {activeTab === 'agents' && (
            <>
              {loadingAgents && (
                <Loading><LoadingSpinner size={25} thickness={2} /></Loading>
              )}
              {!loadingAgents && (
                <Table>
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>E-mail</th>
                      <th>Disponível</th>
                      <th>Peso</th>
                      <th>Rodizio atual</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents && agents.length > 0
                      ? agents.map(agent => <SupportAgentCard key={agent.id} agent={agent} />)
                      : (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                            Nenhum agente no rodízio. Clique em "Adicionar agente" para começar.
                          </td>
                        </tr>
                      )
                    }
                  </tbody>
                </Table>
              )}
            </>
          )}

          {activeTab === 'absences' && (
            <>
              {loadingAbsences && (
                <Loading><LoadingSpinner size={25} thickness={2} /></Loading>
              )}
              {!loadingAbsences && (
                <Table>
                  <thead>
                    <tr>
                      <th>Atendente</th>
                      <th>Motivo</th>
                      <th>Início</th>
                      <th>Retorno</th>
                      <th>Auto-reativar</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absences && absences.length > 0
                      ? absences.map(absence => <AbsenceCard key={absence.id} absence={absence} />)
                      : (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                            Nenhuma ausência cadastrada.
                          </td>
                        </tr>
                      )
                    }
                  </tbody>
                </Table>
              )}
            </>
          )}

          {activeTab === 'shifts' && (
            <>
              {(loadingShifts || loadingDates) && (
                <Loading><LoadingSpinner size={25} thickness={2} /></Loading>
              )}
              {!loadingShifts && !loadingDates && (
                <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {/* Seletor de data */}
                    <div>
                      <SectionTitle>Data do plantão</SectionTitle>
                      <select
                        value={shiftDate}
                        onChange={e => { setSelectedShiftDate(e.target.value); setSelectedShiftUserId(''); }}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '6px',
                          border: `1px solid ${theme.colors.border}`,
                          background: theme.colors.contentBackground,
                          color: theme.colors.text,
                          fontSize: '0.9rem',
                        }}
                      >
                        {(upcomingDates ?? []).map(d => {
                          const label = new Date(d.date + 'T00:00:00').toLocaleDateString('pt-BR', {
                            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
                          });
                          const tag = d.type === 'holiday' ? ' 🏖 Feriado' : ' — 17h–18h';
                          return <option key={d.date} value={d.date}>{label}{tag}</option>;
                        })}
                      </select>
                    </div>

                    {/* Adicionar atendente ao plantão selecionado */}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <select
                        value={selectedShiftUserId}
                        onChange={e => setSelectedShiftUserId(e.target.value)}
                        style={{
                          flex: 1,
                          padding: '0.5rem 0.75rem',
                          borderRadius: '6px',
                          border: `1px solid ${theme.colors.border}`,
                          background: theme.colors.contentBackground,
                          color: theme.colors.text,
                          fontSize: '0.9rem',
                        }}
                      >
                        <option value="">Selecione um atendente...</option>
                        {agentsNotInShift.map(a => (
                          <option key={a.user_id} value={a.user_id}>
                            {a.user?.name ?? a.user_id}
                          </option>
                        ))}
                      </select>
                      <Button
                        small
                        onClick={handleAddShift}
                        disabled={!selectedShiftUserId || addShift.isLoading}
                      >
                        {addShift.isLoading ? '...' : '+ Adicionar'}
                      </Button>
                    </div>
                  </div>

                  <Table>
                    <thead>
                      <tr>
                        <th>Atendente</th>
                        <th>E-mail</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shifts && shifts.length > 0
                        ? shifts.map(shift => (
                          <tr key={shift.id}>
                            <td>{shift.user?.name ?? '—'}</td>
                            <td>{shift.user?.email ?? '—'}</td>
                            <td>
                              <Button small onClick={() => handleDeleteShift(shift)} disabled={deleteShift.isLoading}>
                                <Trash2 color={theme.colors.buttonText} size={16} />
                              </Button>
                            </td>
                          </tr>
                        ))
                        : (
                          <tr>
                            <td colSpan={3} style={{ textAlign: 'center', padding: '2rem' }}>
                              Nenhum atendente registrado para o plantão desta sexta.
                            </td>
                          </tr>
                        )
                      }
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
          {activeTab === 'waiting' && (
            <>
              {loadingWaiting && (
                <Loading><LoadingSpinner size={25} thickness={2} /></Loading>
              )}
              {!loadingWaiting && (
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  <Table>
                    <thead>
                      <tr>
                        <th>Atendente</th>
                        <th>Aguardando</th>
                        <th>Tempo máx.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedWaiting.length > 0
                        ? groupedWaiting.map(group => {
                          const isExpanded = expandedWaitingAgents.has(group.agentId);
                          const maxWait = group.contacts.reduce((max, c) => {
                            const ms = c.last_agent_message_at ? Date.now() - new Date(c.last_agent_message_at).getTime() : 0;
                            return ms > max ? ms : max;
                          }, 0);
                          return (
                            <>
                              <tr
                                key={`wg-${group.agentId}`}
                                onClick={() => toggleWaitingAgent(group.agentId)}
                                style={{ cursor: 'pointer', userSelect: 'none', background: theme.colors.contentBackground }}
                              >
                                <td style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  {isExpanded
                                    ? <ChevronDown size={14} color={theme.colors.primary} />
                                    : <ChevronRight size={14} color={theme.colors.muted} />
                                  }
                                  {group.agentName}
                                </td>
                                <td>{group.contacts.length}</td>
                                <td>{maxWait > 0 ? formatWaitingTime(group.contacts.reduce((oldest, c) => {
                                  const t = c.last_agent_message_at ? new Date(c.last_agent_message_at).getTime() : Infinity;
                                  return t < new Date(oldest).getTime() ? c.last_agent_message_at : oldest;
                                }, group.contacts[0].last_agent_message_at)) : '—'}</td>
                              </tr>
                              {isExpanded && (
                                <>
                                  <tr style={{ background: theme.colors.background }}>
                                    <th style={{ paddingLeft: '2rem', fontSize: '0.78rem', fontWeight: 600, color: theme.colors.muted }}>Contato</th>
                                    <th style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.colors.muted }}>Telefone</th>
                                    <th style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.colors.muted }}>Aguardando há</th>
                                  </tr>
                                  {group.contacts.map(contact => (
                                    <tr key={contact.id} style={{ background: theme.colors.background }}>
                                      <td style={{ paddingLeft: '2rem' }}>{contact.name}</td>
                                      <td>{contact.phone_number}</td>
                                      <td>{formatWaitingTime(contact.last_agent_message_at)}</td>
                                    </tr>
                                  ))}
                                </>
                              )}
                            </>
                          );
                        })
                        : (
                          <tr>
                            <td colSpan={3} style={{ textAlign: 'center', padding: '2rem' }}>
                              Nenhum contato aguardando resposta.
                            </td>
                          </tr>
                        )
                      }
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}

          {activeTab === 'closed' && (
            <>
              {/* Filtro de período */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '1rem 1.5rem 0', flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <SectionTitle style={{ margin: 0, marginRight: '0.25rem', whiteSpace: 'nowrap' }}>Período:</SectionTitle>
                  {dateShortcuts.map(s => (
                    <button
                      key={s.label}
                      onClick={() => { setClosedFrom(s.from); setClosedTo(s.to); }}
                      style={{
                        padding: '0.25rem 0.65rem',
                        borderRadius: '999px',
                        border: `1px solid ${activeShortcut === s.label ? theme.colors.primary : theme.colors.border}`,
                        background: activeShortcut === s.label ? theme.colors.primary : 'transparent',
                        color: activeShortcut === s.label ? theme.colors.buttonText : theme.colors.text,
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        fontWeight: activeShortcut === s.label ? 600 : 400,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="date"
                    value={closedFrom}
                    onChange={e => setClosedFrom(e.target.value)}
                    style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, background: theme.colors.contentBackground, color: theme.colors.text, fontSize: '0.875rem' }}
                  />
                  <span style={{ color: theme.colors.muted, fontSize: '0.85rem' }}>até</span>
                  <input
                    type="date"
                    value={closedTo}
                    onChange={e => setClosedTo(e.target.value)}
                    style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, background: theme.colors.contentBackground, color: theme.colors.text, fontSize: '0.875rem' }}
                  />
                </div>
              </div>

              {loadingClosed && (
                <Loading><LoadingSpinner size={25} thickness={2} /></Loading>
              )}
              {!loadingClosed && (
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  <Table>
                    <thead>
                      <tr>
                        <th>Atendente</th>
                        <th>Atendimentos</th>
                        <th>TME médio</th>
                        <th>TMA médio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedAttendances.length > 0
                        ? groupedAttendances.map(group => {
                          const isExpanded = expandedAgents.has(group.agentId);
                          return (
                            <>
                              {/* Linha-resumo do agente (clicável) */}
                              <tr
                                key={`g-${group.agentId}`}
                                onClick={() => toggleAgent(group.agentId)}
                                style={{ cursor: 'pointer', userSelect: 'none', background: theme.colors.contentBackground }}
                              >
                                <td style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  {isExpanded
                                    ? <ChevronDown size={14} color={theme.colors.primary} />
                                    : <ChevronRight size={14} color={theme.colors.muted} />
                                  }
                                  {group.agentName}
                                </td>
                                <td>{group.records.length}</td>
                                <td>{avgTime(group.records, 'tme')}</td>
                                <td>{avgTime(group.records, 'tma')}</td>
                              </tr>

                              {/* Linhas individuais (visíveis apenas quando expandido) */}
                              {isExpanded && (
                                <>
                                  <tr style={{ background: theme.colors.background }}>
                                    <th style={{ paddingLeft: '2rem', fontSize: '0.78rem', fontWeight: 600, color: theme.colors.muted }}>Contato</th>
                                    <th style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.colors.muted }}>TME</th>
                                    <th style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.colors.muted }}>TMA</th>
                                    <th style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.colors.muted }}>Data · Ações</th>
                                  </tr>
                                  {group.records.map(rec => (
                                    <tr key={rec.id} style={{ background: theme.colors.background }}>
                                      <td style={{ paddingLeft: '2rem' }}>{rec.contact_name}</td>
                                      <td>
                                        {editingId === rec.id
                                          ? <input type="time" value={editTme} onChange={e => setEditTme(e.target.value)} style={{ width: '7rem', padding: '0.2rem 0.4rem', borderRadius: '4px', border: `1px solid ${theme.colors.border}`, background: theme.colors.contentBackground, color: theme.colors.text }} />
                                          : formatTime(rec.tme)
                                        }
                                      </td>
                                      <td>
                                        {editingId === rec.id
                                          ? <input type="time" value={editTma} onChange={e => setEditTma(e.target.value)} style={{ width: '7rem', padding: '0.2rem 0.4rem', borderRadius: '4px', border: `1px solid ${theme.colors.border}`, background: theme.colors.contentBackground, color: theme.colors.text }} />
                                          : formatTime(rec.tma)
                                        }
                                      </td>
                                      <td style={{ whiteSpace: 'nowrap' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <span style={{ color: theme.colors.muted, fontSize: '0.82rem' }}>
                                            {new Date(rec.created_at).toLocaleDateString('pt-BR')}
                                          </span>
                                          {editingId === rec.id ? (
                                            <>
                                              <Button small accent onClick={() => saveEdit(rec.id)} disabled={updateAttendanceTimes.isLoading}>✓</Button>
                                              <Button small onClick={() => setEditingId(null)}>✕</Button>
                                            </>
                                          ) : (
                                            <Button small onClick={() => startEdit(rec.id, rec.tme, rec.tma)}>Editar</Button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              )}
                            </>
                          );
                        })
                        : (
                          <tr>
                            <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                              Nenhum atendimento encerrado no período.
                            </td>
                          </tr>
                        )
                      }
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </ContentTable>
      </Content>

      <AddSupportAgentModal
        isOpen={isAddAgentModalOpen}
        onRequestClose={() => setIsAddAgentModalOpen(false)}
        categoryId={categoryId}
      />

      <AddAbsenceModal
        isOpen={isAddAbsenceModalOpen}
        onRequestClose={() => setIsAddAbsenceModalOpen(false)}
        categoryId={categoryId}
      />
    </Container>
  );
}
